import dayjs from 'dayjs';
import { Report } from 'powerbi-client';
import { useEffect, useMemo, useRef } from 'react';

import { EmbeddedPowerBIReport } from 'views/power-bi-reports/types/embedded-power-bi-report.type';

// Renew the token 2 min before it expires.
const DEFAULT_SAFETY_MARGIN_MS = 2 * 60 * 1000;

type Params = {
  report: EmbeddedPowerBIReport | undefined;
  embeddedReport: Report | undefined;
  refetch: () => void;
  onError?: (error: unknown) => void;
  safetyMarginMs?: number;
};

export function usePowerBITokenRefresh({ report, embeddedReport, refetch, onError, safetyMarginMs = DEFAULT_SAFETY_MARGIN_MS }: Params) {
  const appliedTokenRef = useRef<string | undefined>(undefined);
  const initialTokenRef = useRef<string | undefined>(undefined);

  if (report?.embedToken?.token && !initialTokenRef.current) {
    initialTokenRef.current = report.embedToken.token;
  }

  const tokenEventHandlers = useMemo(() => new Map([['tokenExpired', () => refetch()]]), [refetch]);

  // Apply a freshly fetched token to the live report without re-embedding.
  useEffect(() => {
    if (!embeddedReport || !report?.embedToken?.token) return;
    const { token } = report.embedToken;
    if (appliedTokenRef.current === token) return;
    appliedTokenRef.current = token;
    embeddedReport.setAccessToken(token).catch((error) => onError?.(error));
  }, [embeddedReport, report, onError]);

  // Refetch the embed token before it expires, with a safety margin.
  useEffect(() => {
    if (!report?.embedToken?.expiration) return;
    const msUntilRefresh = dayjs(report.embedToken.expiration).diff(dayjs()) - safetyMarginMs;
    const timer = setTimeout(() => refetch(), Math.max(msUntilRefresh, 0));
    return () => clearTimeout(timer);
  }, [report, refetch, safetyMarginMs]);

  return { initialToken: initialTokenRef.current, tokenEventHandlers };
}
